/**
 * Theme runtime: resolves the user's theme choice (a concrete palette or "system"),
 * applies it as html[data-theme] (which activates the CSS variables in styles.css),
 * persists it, and exposes React hooks so JS-styled surfaces (Cytoscape canvas, charts)
 * can re-render with the active palette. The index.html no-flash script applies the
 * stored choice before first paint; initTheme() re-syncs and arms the OS listener.
 */
import * as React from 'react';
import { THEME_NAMES, themes } from '../tokens.js';
export const THEME_STORAGE_KEY = 'ma_theme';
/**
 * What a visitor who has never chosen sees.
 *
 * Deliberately a concrete palette rather than "system". The product has one intended face: the
 * brand navy canvas is what the palette, the red CTA and the verdict colours were designed
 * against, and it is the register this category reads as native. "system" stays available in
 * the picker for anyone who wants OS-following behaviour, and an explicit choice always wins.
 *
 * The pre-paint script in frontend/index.html MUST agree with this or the page flashes the wrong
 * theme on every cold load; theme-default.test.ts asserts the two stay in step.
 */
export const DEFAULT_THEME_CHOICE = 'dark';
const listeners = new Set();
let media = null;
function notify() {
    for (const l of [...listeners])
        l();
}
export function isThemeName(v) {
    return typeof v === 'string' && THEME_NAMES.includes(v);
}
/** The persisted user choice (the default when unset/invalid). */
export function getThemeChoice() {
    if (typeof localStorage === 'undefined')
        return DEFAULT_THEME_CHOICE;
    try {
        const raw = localStorage.getItem(THEME_STORAGE_KEY);
        if (raw === 'system' || isThemeName(raw))
            return raw;
    }
    catch {
        /* storage unavailable */
    }
    return DEFAULT_THEME_CHOICE;
}
/** OS preference maps to the light/dark pair. */
export function systemTheme() {
    if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches) {
        return 'dark';
    }
    return 'light';
}
export function resolveTheme(choice) {
    return choice === 'system' ? systemTheme() : choice;
}
/** The palette currently active on the document (falls back to the resolved stored choice). */
export function getResolvedTheme() {
    if (typeof document !== 'undefined') {
        const t = document.documentElement.getAttribute('data-theme');
        if (isThemeName(t))
            return t;
    }
    return resolveTheme(getThemeChoice());
}
/** Low-level: set html[data-theme] without persisting. Embeds use this for ?theme= overrides. */
export function applyThemeAttribute(name) {
    if (typeof document === 'undefined')
        return;
    if (document.documentElement.getAttribute('data-theme') !== name) {
        document.documentElement.setAttribute('data-theme', name);
    }
    notify();
}
function ensureMediaListener() {
    if (media || typeof window === 'undefined' || !window.matchMedia)
        return;
    media = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => {
        if (getThemeChoice() === 'system')
            applyThemeAttribute(systemTheme());
    };
    // Older Safari exposes addListener only; both are safe to try.
    if (typeof media.addEventListener === 'function')
        media.addEventListener('change', onChange);
    else if (typeof media.addListener === 'function')
        media.addListener(onChange);
}
/** Persist the user's choice and apply it. */
export function setThemeChoice(choice) {
    try {
        localStorage.setItem(THEME_STORAGE_KEY, choice);
    }
    catch {
        /* storage unavailable */
    }
    ensureMediaListener();
    applyThemeAttribute(resolveTheme(choice));
}
/** Boot-time sync (idempotent with the index.html no-flash script) + OS-change tracking. */
export function initTheme() {
    ensureMediaListener();
    applyThemeAttribute(resolveTheme(getThemeChoice()));
}
function subscribe(fn) {
    ensureMediaListener();
    listeners.add(fn);
    return () => {
        listeners.delete(fn);
    };
}
// Server snapshot for useSyncExternalStore: must match the pre-paint default, or the first
// client render disagrees with the markup the browser already painted.
const serverTheme = 'dark';
const serverChoice = DEFAULT_THEME_CHOICE;
export function useTheme() {
    const theme = React.useSyncExternalStore(subscribe, getResolvedTheme, () => serverTheme);
    const choice = React.useSyncExternalStore(subscribe, getThemeChoice, () => serverChoice);
    return { choice, theme, setChoice: setThemeChoice };
}
/** The active palette object (exact same values as the CSS variables) for JS-styled surfaces. */
export function useThemeTokens() {
    const { theme } = useTheme();
    return themes[theme];
}
//# sourceMappingURL=theme.js.map